import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from 'react-i18next';
import {getNodeColor, NodeCategory, NodeCategoryConfig} from '../../config/nodeCategories';
import {getNodeOutputs} from '../../config/nodeOutputs';
import {NODE_COPY, outputNameSx} from './nodeCopy';

const OUTPUT_PREVIEW_LIMIT = 6;

interface NodeReferenceDialogProps {
    open: boolean;
    onClose: () => void;
    nodeCategories: NodeCategory[];
}

const NodeReferenceEntry: React.FC<{node: NodeCategoryConfig}> = ({node}) => {
    const {t} = useTranslation();
    const copy = NODE_COPY[node.type];
    const outputs = getNodeOutputs(node.type);

    return (
        <Stack gap={0.75}>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
                <Box sx={{color: getNodeColor(node.type), display: 'flex'}}>
                    {node.icon}
                </Box>
                <Typography variant={'subtitle2'}>
                    {t(node.label)}
                </Typography>
            </Stack>

            {!!copy && (
                <Typography variant={'body2'} color={'text.secondary'}>
                    {t(copy.detail)}
                </Typography>
            )}

            {outputs.length > 0 && (
                <Stack gap={0.5} sx={{pt: 0.25}}>
                    <Typography variant={'overline'} color={'text.secondary'}>
                        {t('Outputs')}
                    </Typography>

                    {outputs.slice(0, OUTPUT_PREVIEW_LIMIT).map(output => (
                        <Stack
                            key={output.name}
                            direction={'row'}
                            alignItems={'baseline'}
                            gap={0.75}
                            flexWrap={'wrap'}
                        >
                            <Box component={'span'} sx={outputNameSx}>{output.name}</Box>
                            {!!output.description && (
                                <Typography variant={'body2'} color={'text.secondary'}>
                                    {output.description}
                                </Typography>
                            )}
                        </Stack>
                    ))}

                    {outputs.length > OUTPUT_PREVIEW_LIMIT && (
                        <Typography variant={'body2'} color={'text.disabled'}>
                            {t('+{{total}} more', {total: outputs.length - OUTPUT_PREVIEW_LIMIT})}
                        </Typography>
                    )}
                </Stack>
            )}
        </Stack>
    );
};

const NodeReferenceDialog: React.FC<NodeReferenceDialogProps> = ({open, onClose, nodeCategories}) => {
    const {t} = useTranslation();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={'sm'}
            fullWidth
            PaperProps={{sx: {maxHeight: '90vh'}}}
        >
            <DialogTitle>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                    {t('Node reference')}

                    <Tooltip title={t('Close')}>
                        <IconButton onClick={onClose} size={'small'} aria-label={t('Close')}>
                            <CloseIcon/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                <Stack gap={3} sx={{py: 1}}>
                    {nodeCategories.map(category => (
                        <Stack key={category.name} gap={2}>
                            <Typography variant={'overline'} color={'text.secondary'}>
                                {t(category.name)}
                            </Typography>

                            <Stack gap={2} divider={<Divider flexItem/>}>
                                {category.nodes.map(node => (
                                    <NodeReferenceEntry key={node.type} node={node}/>
                                ))}
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    {t('Close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NodeReferenceDialog;
